import brand from '@helpers/brand';
import { fluidAdjusted } from '@helpers/fluid';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { gradientStyle, headingStyles, paragraphMedium } from '@helpers/typography';
import BurnEffect from '@img/burn-effect.png';
import HexagonSvg from '@img/shapes/hexagon-solid-squash.svg';
import TopLeftSvg from '@img/shapes/shape-bottom-right.svg';
import BottomRightSvg from '@img/shapes/shape-group.svg';
import SectionWrapperStyles from '@stories/Components/Global/SectionWrapper/SectionWrapper.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.section `
  --bgColor: ${brand.theme.orange};
  overflow: hidden;

  background-color: var(--bgColor);
  position: relative;
  display: block;

  &[data-theme='blue'] {
    --bgColor: ${brand.theme.blue};
  }

  &[data-theme='pink'] {
    --bgColor: ${brand.theme.pink};
  }

  &[data-theme='green'] {
    --bgColor: ${brand.theme.green};
  }

  ${SectionWrapperStyles.Container} {
    --sectionSpacingTop: 120px;
    --sectionSpacingBottom: 40px;

    @media ${from(Device.Tablet)} {
      --sectionSpacingTop: 224px;
      --sectionSpacingBottom: 100px;
    }
  }
`;
const ShapeTopLeft = styled(TopLeftSvg) `
  --color: ${rgba(brand.buff.buff4, 0.62)};
  color: var(--color);

  position: absolute;
  top: 0;
  left: 0;
  height: 45%;
  z-index: 1;

  @media ${until(Device.Tablet)} {
    height: 25%;
  }

  [data-theme='blue'] & {
    --color: ${rgba(brand.tertiary.blue, 0.22)};
  }

  [data-theme='pink'] & {
    --color: ${rgba(brand.tertiary.pink, 0.22)};
  }

  [data-theme='green'] & {
    --color: ${rgba(brand.tertiary.green, 0.22)};
  }
`;
const ShapeBottomRight = styled(BottomRightSvg) `
  --color: ${rgba(brand.buff.buff4, 0.42)};
  color: var(--color);

  position: absolute;
  bottom: 0;
  right: -20px;
  height: 100%;
  z-index: 1;

  [data-theme='blue'] & {
    --color: ${rgba(brand.tertiary.blue, 0.12)};
  }

  [data-theme='pink'] & {
    --color: ${rgba(brand.tertiary.pink, 0.12)};
  }

  [data-theme='green'] & {
    --color: ${rgba(brand.tertiary.green, 0.12)};
  }
`;
const Grid = styled.div `
  ${baseGrid};
  align-items: center;
  position: relative;
  z-index: 2;

  @media ${until(Device.Tablet)} {
    row-gap: 32px;
  }
`;
const BreadCrumbArea = styled.div `
  position: absolute;
  top: -40px;

  @media ${from(Device.Tablet)} {
    top: -30px;
  }
`;
const ContentArea = styled.div `
  grid-column: span 12;
  grid-row: 2;

  @media ${from(Device.Tablet)} {
    grid-column: span 6;
    grid-row: 1;
  }
`;
const Title = styled.h1 `
  ${headingStyles};
  --titleColor: ${brand.secondary.red5};
  font-size: ${fluidAdjusted(32, 64)};
  line-height: ${fluidAdjusted(40, 74)};
  color: var(--titleColor);

  &[data-title-theme='mid'] {
    --titleColor: ${brand.secondary.red3};
  }

  &[data-title-theme='light'] {
    --titleColor: ${brand.secondary.red1};
  }

  &[data-title-theme='gradient'] {
    ${gradientStyle};
  }
`;
const Intro = styled.div `
  ${paragraphMedium};
  max-width: 700px;
`;
const CtaArea = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 20px;
`;
const MediaArea = styled.div `
  grid-column: span 12;
  grid-row: 1;
  display: grid;

  @media ${from(Device.Tablet)} {
    grid-column: 8 / span 5;
    justify-content: end;
  }

  @media ${from(Device.DesktopLarge)} {
    grid-column: 7 / span 6;
  }
`;
const MediaHolder = styled.div `
  max-width: 700px;
  position: relative;
`;
const Image = styled.img `
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;
const BurnOverlay = styled.div `
  width: 123%;
  height: 144%;
  background-image: url(${BurnEffect});
  background-position: center;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(8%, -50%);
  mix-blend-mode: lighten;

  @media ${until(Device.TabletSmall)} {
    transform: translate(20%, -50%);
  }
`;
const ShapeShadow = styled(HexagonSvg) `
  position: absolute;
  color: ${brand.white};
  transform: translate(12px, 20px);
  max-width: 700px;

  @media ${until(Device.Tablet)} {
    transform: translate(6px, 10px);
  }
`;
const InnerHeroBannerStyles = {
    BreadCrumbArea,
    BurnOverlay,
    Container,
    ContentArea,
    CtaArea,
    Grid,
    Image,
    Intro,
    MediaArea,
    MediaHolder,
    ShapeShadow,
    ShapeBottomRight,
    ShapeTopLeft,
    Title,
};
export default InnerHeroBannerStyles;
